import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Row, Col, ProgressBar } from 'react-bootstrap';
import { FaKey, FaSyncAlt, FaCheckCircle, FaClock, FaExclamationTriangle, FaEnvelope } from 'react-icons/fa';
import axios from 'axios';

const LicenseManagement = () => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const fetchLicenseStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/license/status', {
        headers: { 'x-auth-token': token }
      });
      setLicenseStatus(response.data);
    } catch (err) {
      console.error('Error fetching license status:', err);
      setError('Unable to check license status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!licenseStatus) return <Badge bg="secondary">Unknown</Badge>;
    
    switch (licenseStatus.status) {
      case 'ACTIVE':
        return <Badge bg="success" style={{ fontSize: '1rem', padding: '8px 16px' }}><FaCheckCircle className="me-1" />Active</Badge>;
      case 'EXPIRED':
        return <Badge bg="danger" style={{ fontSize: '1rem', padding: '8px 16px' }}><FaExclamationTriangle className="me-1" />Expired</Badge>;
      case 'NO_LICENSE':
        return <Badge bg="warning" style={{ fontSize: '1rem', padding: '8px 16px' }}><FaKey className="me-1" />No License</Badge>;
      default:
        return <Badge bg="secondary" style={{ fontSize: '1rem', padding: '8px 16px' }}>Unknown</Badge>;
    }
  };

  const getProgressVariant = () => {
    if (!licenseStatus || !licenseStatus.daysRemaining) return 'secondary';
    
    if (licenseStatus.daysRemaining <= 3) return 'danger';
    if (licenseStatus.daysRemaining <= 7) return 'warning';
    if (licenseStatus.daysRemaining <= 14) return 'info';
    return 'success';
  };

  const getProgressPercentage = () => {
    if (!licenseStatus || !licenseStatus.daysRemaining) return 0;
    
    const totalDays = licenseStatus.licenseType === 'TRIAL' ? 60 : 
                      licenseStatus.licenseType === 'YEARLY' ? 365 : 180;
    return Math.max(0, Math.min(100, (licenseStatus.daysRemaining / totalDays) * 100));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary me-2" role="status"></div>
        <span>Checking license status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <FaExclamationTriangle className="me-2" />
        {error}
        <Button variant="link" onClick={fetchLicenseStatus}>Retry</Button>
      </Alert>
    );
  }

  return (
    <div className="license-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaKey className="me-2" />
          License Status
        </h2>
        <Button 
          variant="outline-primary"
          onClick={fetchLicenseStatus}
          disabled={loading}
        >
          <FaSyncAlt className={`me-1 ${loading ? 'fa-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Row>
        <Col lg={8}>
          {/* Main License Status Card */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">License Information</h5>
              {getStatusBadge()}
            </Card.Header>
            <Card.Body>
              {licenseStatus?.isValid ? (
                <>
                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="text-muted small">Company</label>
                        <h5>{licenseStatus.customerName}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="text-muted small">License Type</label>
                        <h5>
                          <Badge bg={licenseStatus.licenseType === 'TRIAL' ? 'info' : 
                                    licenseStatus.licenseType === 'YEARLY' ? 'primary' : 'success'}>
                            {licenseStatus.licenseType === 'TRIAL' ? 'Trial' : 
                             licenseStatus.licenseType === 'YEARLY' ? 'Yearly' : 'Standard'}
                          </Badge>
                        </h5>
                      </div>
                    </Col>
                  </Row>
                  
                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="text-muted small">Expiration Date</label>
                        <h5>{new Date(licenseStatus.expirationDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="text-muted small">Days Remaining</label>
                        <h5 className={licenseStatus.daysRemaining <= 7 ? 'text-danger' : 
                                       licenseStatus.daysRemaining <= 14 ? 'text-warning' : 'text-success'}>
                          {licenseStatus.daysRemaining} days
                        </h5>
                      </div>
                    </Col>
                  </Row>
                  
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">License Period</span>
                      <span className="text-muted">{licenseStatus.daysRemaining} days remaining</span>
                    </div>
                    <ProgressBar 
                      now={getProgressPercentage()} 
                      variant={getProgressVariant()}
                      style={{ height: '10px' }}
                    />
                  </div>
                  
                  {licenseStatus.warningNeeded && (
                    <Alert variant="warning" className="mt-4">
                      <FaExclamationTriangle className="me-2" />
                      <strong>License Expiring Soon!</strong>
                      <p className="mb-0 mt-2">
                        Your license will expire in {licenseStatus.daysRemaining} day{licenseStatus.daysRemaining !== 1 ? 's' : ''}. 
                        Please contact support to renew your license.
                      </p>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  {licenseStatus?.status === 'EXPIRED' ? (
                    <Alert variant="danger">
                      <FaExclamationTriangle size={32} className="mb-3" />
                      <h5>License Expired</h5>
                      <p>
                        Your license expired on {licenseStatus.expirationDate ? 
                          new Date(licenseStatus.expirationDate).toLocaleDateString() : 'unknown date'}.
                      </p>
                      <p>The application is now in read-only mode. Please contact support to renew your license.</p>
                    </Alert>
                  ) : (
                    <Alert variant="warning">
                      <FaKey size={32} className="mb-3" />
                      <h5>No License Found</h5>
                      <p>Your account does not have an active license assigned.</p>
                      <p>Please contact support to activate your license.</p>
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          {/* Support Card */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">
                <FaEnvelope className="me-2" />
                Need Help?
              </h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted">
                For license renewals, questions, or support, please contact:
              </p>
              <div className="mb-3">
                <strong>Email:</strong><br />
                <a href="mailto:support@constructionmgmt.com">support@constructionmgmt.com</a>
              </div>
              <div className="mb-3">
                <strong>Phone:</strong><br />
                <span>(555) 123-4567</span>
              </div>
              <hr />
              <p className="text-muted small mb-0">
                Business hours: Monday - Friday, 9am - 5pm EST
              </p>
            </Card.Body>
          </Card>
          
          {/* License Types Info */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">
                <FaKey className="me-2" />
                License Types
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Badge bg="info" className="me-2">Trial</Badge>
                <span className="text-muted">60 days</span>
                <p className="small text-muted mb-0 mt-1">
                  Full access for evaluation purposes
                </p>
              </div>
              <div className="mb-3">
                <Badge bg="success" className="me-2">Standard</Badge>
                <span className="text-muted">6 months</span>
                <p className="small text-muted mb-0 mt-1">
                  Full access for your team
                </p>
              </div>
              <div>
                <Badge bg="primary" className="me-2">Yearly</Badge>
                <span className="text-muted">12 months</span>
                <p className="small text-muted mb-0 mt-1">
                  Best value for long-term use
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LicenseManagement;
